import React, { useState } from 'react'
import { Box, Typography, IconButton, TextField, Button, CircularProgress } from '@mui/material'
import { AccessTimeOutlined, EditOutlined, DeleteOutline } from '@mui/icons-material'

/**
 * Generic list component to render timestamped text entries with optional edit/delete.
 * Props:
 * - entries: Array<{ timestamp?: string, text?: string, user_id?: number | string }>
 * - editablePredicate?: (entry, index) => boolean
 * - onEdit?: (index, newText) => (void | Promise<void>)
 * - onDelete?: (index) => (void | Promise<void>)
 * - renderEntryHeader?: (entry, index) => ReactNode   // custom header row (e.g., username • timestamp)
 * - resolveUserName?: (entry) => string               // used by default header to render name
 * - emptyMessage?: string
 * - textLeftPadding?: number                          // left padding for body text (default 2.5)
 * - containerSx?: object                              // MUI sx for container wrapper
 */
const TimeStampedEntriesList = ({
  entries = [],
  editablePredicate = () => false,
  onEdit,
  onDelete,
  renderEntryHeader,
  resolveUserName,
  emptyMessage = 'No entries yet.',
  textLeftPadding = 2.5,
  containerSx = {},
}) => {
  const [editingIndex, setEditingIndex] = useState(null)
  const [editText, setEditText] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const startEditing = (index, entry) => {
    setEditingIndex(index)
    setEditText(entry?.text ?? '')
  }

  const cancelEditing = () => {
    setEditingIndex(null)
    setEditText('')
  }

  const handleSave = async (index) => {
    const trimmed = editText.trim()
    if (!trimmed || typeof onEdit !== 'function') return
    try {
      setIsSaving(true)
      const maybePromise = onEdit(index, trimmed)
      if (maybePromise && typeof maybePromise.then === 'function') {
        await maybePromise
      }
      cancelEditing()
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (index) => {
    if (typeof onDelete !== 'function') return
    const maybePromise = onDelete(index)
    if (maybePromise && typeof maybePromise.then === 'function') {
      await maybePromise
    }
  }

  const defaultHeader = (entry) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        marginBottom: '0px !important',
        paddingBottom: '0px !important',
      }}
    >
      <AccessTimeOutlined sx={{ fontSize: '14px', color: '#656D90' }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#656D90' }}>
        {entry?.timestamp || ''}
      </Typography>
      {entry?.user_id ? (
        <>
          <Typography sx={{ fontSize: '10px', color: '#999' }}>•</Typography>
          <Typography sx={{ fontSize: '10px', color: '#999' }}>
            {typeof resolveUserName === 'function' ? resolveUserName(entry) : ''}
          </Typography>
        </>
      ) : null}
    </Box>
  )

  if (!Array.isArray(entries) || entries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: '#999', ...containerSx }}>
        <Typography sx={{ fontSize: '12px', color: '#656A7E' }}>{emptyMessage}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 1, ...containerSx }}>
      {entries.map((entry, index) => {
        const canEdit = editablePredicate(entry, index) === true
        const isCurrentlyEditing = editingIndex === index

        return (
          <Box
            key={index}
            sx={{
              marginBottom: '6px',
              paddingBottom: '6px',
              borderBottom: index < entries.length - 1 ? '1px solid #E8EAF6' : 'none',
            }}
          >
            {isCurrentlyEditing ? (
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: '#FCFBFF',
                  border: '1px solid #DDE1F3',
                  borderRadius: '4px',
                }}
              >
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  rows={2}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleSave(index)
                    } else if (e.key === 'Escape') {
                      cancelEditing()
                    }
                  }}
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '12px',
                      backgroundColor: '#fff',
                      '& fieldset': {
                        borderColor: '#DDE1F3',
                      },
                    },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={cancelEditing}
                    sx={{
                      fontSize: '11px',
                      textTransform: 'none',
                      borderColor: '#DDE1F3',
                      color: '#656D90',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleSave(index)}
                    disabled={!editText.trim() || isSaving}
                    startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={{ fontSize: '11px', textTransform: 'none' }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 0.5,
                    marginBottom: '4px',
                    paddingBottom: '4px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '0px !important',
                      paddingBottom: '0px !important',
                    }}
                  >
                    {typeof renderEntryHeader === 'function'
                      ? renderEntryHeader(entry, index)
                      : defaultHeader(entry)}
                  </Box>
                  {canEdit && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => startEditing(index, entry)}
                        sx={{
                          padding: '4px',
                          color: '#656D90',
                          '&:hover': { backgroundColor: '#F5F5F5' },
                        }}
                      >
                        <EditOutlined sx={{ fontSize: '14px' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(index)}
                        sx={{
                          padding: '4px',
                          color: '#656D90',
                          '&:hover': { backgroundColor: '#FFE5E5', color: '#d32f2f' },
                        }}
                      >
                        <DeleteOutline sx={{ fontSize: '14px' }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#656A7E',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    // pl: textLeftPadding,
                  }}
                >
                  {entry?.text}
                </Typography>
              </>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default React.memo(TimeStampedEntriesList)
